import type { TFunction } from "i18next";
import React from "react";
import { Link } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";
import type { User } from "../../types/types";
import type {
  AttributeSchema,
  AttributeValue,
  ProductReference,
} from "../../types/types.PIM";
import { useAuthContext } from "../Auth";
import { CardProductCompact } from "../CardProductCompact/CardProductCompact";
import {
  NonClickableProductCard,
  ProductCardSingleWrapper,
} from "../CardProductCompact/CardProductCompact";
import {
  getReferenceRestrictedMessage,
  isProductReferenceValueDisplayAccessible,
} from "../ObjectReference/utils";
import type { ReferenceDisplayAccessContext } from "../ObjectReference/utils";
import { ProductReferenceMockTooltip } from "../ProductReferenceMockTooltip/ProductReferenceMockTooltip";
import { ProductRefEmblaCarousel } from "./ProductRefEmblaCarousel";

const InnerContentText = styled.p`
  color: ${({ theme }) => theme.primaryTextColor};
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: ${({ theme }) => theme.fontSizes.medium};
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.33;
  letter-spacing: normal;
  margin: 0;
`;

const StyledUL = styled.ul.attrs({
  className: "object_reference_list",
})<{ $backgroundColor?: string }>`
  padding-left: ${({ theme }) => theme.resolve("spacing_md")};
  margin: 5px 0 10px;
  margin-left: 13px;
  li {
    font-size: ${({ theme }) => theme.fontSizes.medium};
    line-height: 1.33;
    padding-bottom: 5px;

    span,
    a {
      margin-left: -2px;
      position: relative;
      ${({ $backgroundColor }) =>
        $backgroundColor
          ? `
        background-color: ${$backgroundColor};
        border-radius: 2px;
        padding: 2px 4px;
      `
          : ""}
    }

    &::marker {
      font-size: ${({ theme }) => theme.fontSizes.regular};
    }
  }
`;

type ProductReferenceAttributeSchema = Extract<
  AttributeSchema,
  { input_type: "product_reference" }
>;

type ProductReferenceAttributeDisplayProps = {
  attribute: ProductReferenceAttributeSchema;
  isPortfolio: boolean;
  storePath: string;
  adminPath: string;
  user: User | null | undefined;
  roleIsSellerAdmin: boolean;
  backgroundColor?: string;
  t: TFunction;
};

export type ProductReferencePathOptions = {
  isPortfolio: boolean;
  storePath: string;
  adminPath: string;
};

export const getProductReferenceHref = (
  product: ProductReference,
  { isPortfolio, storePath, adminPath }: ProductReferencePathOptions
): string =>
  isPortfolio
    ? `${storePath}/product/${product.slug}`
    : `${adminPath}/pim/products/${product.product_number ?? product.id}`;

function getProductRefFromValue(
  value: AttributeValue
): ProductReference | undefined {
  return value.product_ref ?? undefined;
}

function renderInaccessibleProductCard(
  productRef: ProductReference,
  t: TFunction
): React.ReactNode {
  return (
    <NonClickableProductCard>
      <CardProductCompact
        product={productRef}
        variant="tooltip"
        dynamicPreviewBlocks={[
          {
            header: "",
            content: getReferenceRestrictedMessage(productRef, t),
          },
        ]}
      />
    </NonClickableProductCard>
  );
}

function renderProductReferenceLinkRow(
  value: AttributeValue,
  index: number,
  options: {
    pathOptions: ProductReferencePathOptions;
    accessContext: ReferenceDisplayAccessContext;
    backgroundColor?: string;
    t: TFunction;
  },
  accessibleValueIds?: Set<string>
): React.ReactNode {
  const { pathOptions, accessContext, t } = options;
  const { isPortfolio, storePath, adminPath } = pathOptions;
  const productRef = getProductRefFromValue(value);
  if (!productRef) return null;

  const isAccessible =
    accessibleValueIds != null
      ? accessibleValueIds.has(value.id ?? "")
      : isProductReferenceValueDisplayAccessible(productRef, accessContext);
  const listItemKey = value.id || `product-ref-${index}`;

  if (isAccessible) {
    const href = getProductReferenceHref(productRef, pathOptions);
    return (
      <li key={listItemKey}>
        <ProductReferenceMockTooltip
          productRef={productRef}
          isPortfolio={isPortfolio}
          storePath={storePath}
          adminPath={adminPath}
        >
          <Link to={href}>{productRef.name}</Link>
        </ProductReferenceMockTooltip>
      </li>
    );
  }

  return (
    <li key={listItemKey}>
      <span
        data-tip={getReferenceRestrictedMessage(productRef, t)}
        data-for={value.id}
      >
        {productRef.name}
      </span>
      <ReactTooltip delayHide={200} id={value.id} effect="solid" />
    </li>
  );
}

export function ProductReferenceAttributeDisplay({
  attribute,
  isPortfolio,
  storePath,
  adminPath,
  user,
  roleIsSellerAdmin,
  backgroundColor,
  t,
}: ProductReferenceAttributeDisplayProps) {
  const { hasPermission } = useAuthContext();

  if (!attribute.values?.length) {
    return <InnerContentText>--</InnerContentText>;
  }

  const pathOptions: ProductReferencePathOptions = {
    isPortfolio,
    storePath,
    adminPath,
  };
  const accessContext: ReferenceDisplayAccessContext = {
    isPortfolio,
    user,
    roleIsSellerAdmin,
    hasPermission,
  };

  const hasCardDisplay =
    "display_configuration" in attribute &&
    attribute.display_configuration === "product_card";

  const valuesWithRef = attribute.values.filter((value) =>
    Boolean(getProductRefFromValue(value))
  );

  if (!valuesWithRef.length) {
    return <InnerContentText>--</InnerContentText>;
  }

  const isValueAccessible = (value: AttributeValue): boolean => {
    const productRef = getProductRefFromValue(value);
    return (
      Boolean(productRef) &&
      isProductReferenceValueDisplayAccessible(productRef!, accessContext)
    );
  };

  const accessibleValueIds = new Set(
    valuesWithRef
      .filter(isValueAccessible)
      .map((value) => value.id ?? "")
      .filter(Boolean)
  );

  const inaccessibleProductIds = new Set(
    valuesWithRef
      .filter((value) => !isValueAccessible(value))
      .map((value) => getProductRefFromValue(value)!.id)
  );

  const listOptions = {
    pathOptions,
    accessContext,
    backgroundColor,
    t,
  };

  const handleProductClick = (product: ProductReference) => {
    window.location.href = getProductReferenceHref(product, pathOptions);
  };

  if (hasCardDisplay) {
    if (valuesWithRef.length === 1) {
      const productRef = getProductRefFromValue(valuesWithRef[0]);
      if (productRef) {
        return (
          <ProductCardSingleWrapper>
            {inaccessibleProductIds.has(productRef.id) ? (
              renderInaccessibleProductCard(productRef, t)
            ) : (
              <CardProductCompact
                product={productRef}
                onProductClick={handleProductClick}
              />
            )}
          </ProductCardSingleWrapper>
        );
      }
    }

    const products = valuesWithRef
      .map(getProductRefFromValue)
      .filter((product): product is ProductReference => Boolean(product));

    if (products.length > 1) {
      return (
        <ProductRefEmblaCarousel
          products={products}
          onProductClick={handleProductClick}
          inaccessibleProductIds={inaccessibleProductIds}
          dynamicPreviewBlocksByProductId={Object.fromEntries(
            valuesWithRef.map((value) => {
              const productRef = getProductRefFromValue(value)!;
              return [
                productRef.id,
                inaccessibleProductIds.has(productRef.id)
                  ? [
                      {
                        header: "",
                        content: getReferenceRestrictedMessage(productRef, t),
                      },
                    ]
                  : [],
              ];
            })
          )}
        />
      );
    }
  }

  return (
    <StyledUL $backgroundColor={backgroundColor}>
      {valuesWithRef.map((value, index) =>
        renderProductReferenceLinkRow(
          value,
          index,
          listOptions,
          accessibleValueIds
        )
      )}
    </StyledUL>
  );
}
